import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '@/services/api';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import ReportDateRangeFilter from '@/components/reports/ReportDateRangeFilter';
import Pagination, { LIST_PAGE_SIZE } from '@/components/common/Pagination';
import ReportExportButton from '@/components/reports/ReportExportButton';
import { downloadCsv } from '@/utils/csv';
import { formatMoney, formatDate, toLocalDateString } from '@/utils/currency';

type FulfillmentFilter = 'All' | 'Pickup' | 'Delivery';

export default function ScheduledFulfillmentsSection() {
  const {
    dateRange,
    setDateRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    from_date,
    to_date,
  } = useReportDateRange('Today');

  const [fulfillmentType, setFulfillmentType] = useState<FulfillmentFilter>('All');
  const [page, setPage] = useState(0);
  const today = toLocalDateString();

  const { data, isLoading } = useQuery({
    queryKey: [
      'scheduledFulfillments',
      from_date ? String(from_date) : '',
      to_date ? String(to_date) : '',
      fulfillmentType,
    ],
    queryFn: () =>
      reportsApi.getScheduledFulfillments({
        from_date: String(from_date),
        to_date: String(to_date),
        fulfillment_type: fulfillmentType === 'All' ? undefined : fulfillmentType,
      }),
    enabled: Boolean(from_date && to_date),
  });

  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const summary = data?.summary || {
    total_count: 0,
    pickup_count: 0,
    delivery_count: 0,
    created_in_past_count: 0,
    total_value: 0,
  };

  const currentData = useMemo(
    () => rows.slice(page * LIST_PAGE_SIZE, (page + 1) * LIST_PAGE_SIZE),
    [rows, page],
  );

  const handleExport = () => {
    if (!rows.length) return;
    const csvData = rows.map((row: any) => ({
      invoice_id: row.invoice_id,
      sale_date: formatDate(row.sale_date),
      delivery_date: formatDate(row.delivery_date),
      fulfillment_type: row.fulfillment_type || 'Pickup',
      customer: row.customer_name || 'Walk in',
      contact: row.customer_contact || '',
      total: row.total || 0,
      status: row.status || '',
      created_at: formatDate(row.created_at),
      created_by: row.created_by || '',
      created_in_past: Number(row.created_in_past) ? 'Yes' : 'No',
      address: row.delivery_address || '',
      post_code: row.delivery_postal_code || '',
    }));
    downloadCsv(`Scheduled_Fulfillments_${from_date}_to_${to_date}.csv`, [
      { key: 'invoice_id', label: 'Invoice' },
      { key: 'sale_date', label: 'Sale Date' },
      { key: 'delivery_date', label: 'Pick / Delivery Date' },
      { key: 'fulfillment_type', label: 'Type' },
      { key: 'customer', label: 'Customer' },
      { key: 'contact', label: 'Contact' },
      { key: 'total', label: 'Total' },
      { key: 'status', label: 'Status' },
      { key: 'created_at', label: 'Created' },
      { key: 'created_by', label: 'Created By' },
      { key: 'created_in_past', label: 'Created Earlier' },
      { key: 'address', label: 'Address' },
      { key: 'post_code', label: 'Post Code' },
    ], csvData);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col xl:flex-row gap-4 items-start xl:items-center justify-between bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
        <ReportDateRangeFilter
          dateRange={dateRange}
          onDateRangeChange={(v) => {
            setDateRange(v);
            setPage(0);
          }}
          customFrom={customFrom}
          onCustomFromChange={(v) => {
            setCustomFrom(v);
            setPage(0);
          }}
          customTo={customTo}
          onCustomToChange={(v) => {
            setCustomTo(v);
            setPage(0);
          }}
        />
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden text-xs font-semibold">
            {(['All', 'Pickup', 'Delivery'] as FulfillmentFilter[]).map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => {
                  setFulfillmentType(opt);
                  setPage(0);
                }}
                className={`px-3 py-2 transition-colors ${
                  fulfillmentType === opt
                    ? 'bg-slate-900 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
          <ReportExportButton onClick={handleExport} disabled={!rows.length} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
          <p className="text-sm font-medium text-gray-500 mb-1">Scheduled</p>
          <p className="text-2xl font-bold text-gray-900">{summary.total_count || 0}</p>
        </div>
        <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
          <p className="text-sm font-medium text-gray-500 mb-1">Pickups / Deliveries</p>
          <p className="text-2xl font-bold text-gray-900">
            {summary.pickup_count || 0}
            <span className="text-gray-300 mx-1">/</span>
            {summary.delivery_count || 0}
          </p>
        </div>
        <div className="bg-white p-5 rounded-xl border border-amber-100 shadow-sm">
          <p className="text-sm font-medium text-amber-700 mb-1">Created earlier</p>
          <p className="text-2xl font-bold text-amber-900">{summary.created_in_past_count || 0}</p>
        </div>
        <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
          <p className="text-sm font-medium text-gray-500 mb-1">Total Value</p>
          <p className="text-2xl font-bold text-gray-900">{formatMoney(summary.total_value || 0)}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col">
        <div className="overflow-x-auto min-h-[400px]">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="p-4 font-medium text-gray-600">Invoice</th>
                <th className="p-4 font-medium text-gray-600">Sale Date</th>
                <th className="p-4 font-medium text-gray-600">Pick / Delivery</th>
                <th className="p-4 font-medium text-gray-600">Type</th>
                <th className="p-4 font-medium text-gray-600">Customer</th>
                <th className="p-4 font-medium text-gray-600 text-right">Total</th>
                <th className="p-4 font-medium text-gray-600">Status</th>
                <th className="p-4 font-medium text-gray-600">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-gray-500">
                    Loading scheduled pickups / deliveries...
                  </td>
                </tr>
              ) : currentData.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-gray-500">
                    No pickups or deliveries scheduled for this period.
                  </td>
                </tr>
              ) : (
                currentData.map((row: any) => {
                  const due = String(row.delivery_date || '').slice(0, 10);
                  const isFutureDue = due > today;
                  const isTodayDue = due === today;
                  return (
                    <tr
                      key={row.invoice_id}
                      className={
                        isTodayDue
                          ? 'bg-amber-50/60'
                          : isFutureDue
                            ? 'bg-sky-50/40'
                            : 'hover:bg-gray-50/50'
                      }
                    >
                      <td className="p-4 font-medium text-primary-600">{row.invoice_id}</td>
                      <td className="p-4 text-gray-600 whitespace-nowrap">{formatDate(row.sale_date) || '—'}</td>
                      <td className="p-4 whitespace-nowrap">
                        <span
                          className={
                            isTodayDue || isFutureDue
                              ? 'inline-flex px-2 py-0.5 rounded-md text-xs font-semibold bg-amber-100 text-amber-900 border border-amber-200'
                              : 'text-gray-700'
                          }
                        >
                          {formatDate(row.delivery_date) || '—'}
                        </span>
                      </td>
                      <td className="p-4 text-gray-600">{row.fulfillment_type || 'Pickup'}</td>
                      <td className="p-4">
                        <div className="text-gray-900 font-medium">{row.customer_name || 'Walk in'}</div>
                        {row.customer_contact && (
                          <div className="text-xs text-gray-400 mt-0.5">{row.customer_contact}</div>
                        )}
                      </td>
                      <td className="p-4 text-right font-semibold text-gray-900">
                        {formatMoney(row.total || 0)}
                      </td>
                      <td className="p-4 text-gray-600">{row.status}</td>
                      <td className="p-4 text-gray-500 whitespace-nowrap">
                        <div>{formatDate(row.created_at) || '—'}</div>
                        {Number(row.created_in_past) === 1 && (
                          <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 mt-0.5">
                            Ahead of due
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {rows.length > LIST_PAGE_SIZE && (
          <Pagination
            page={page}
            pageSize={LIST_PAGE_SIZE}
            total={rows.length}
            onPageChange={setPage}
          />
        )}
      </div>
    </div>
  );
}
